import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { mcpListParams, type AuthorizationValues, type LocalMcpProps, type OrganizationOption } from '@/frontend/features/LocalMcp/types'

type Props = {
  options: LocalMcpProps['organization_options']
  values: AuthorizationValues
  selected: OrganizationOption[]
  copy: LocalMcpProps['copy']
  filters: LocalMcpProps['filters']
  listPage: number
  disabled: boolean
  onChange: (values: Partial<AuthorizationValues>) => void
}
export default function OrganizationPicker({ options, values, selected, copy, filters, listPage, disabled, onChange }: Props) {
  const { url } = usePage()
  const [query, setQuery] = useState(options.query)
  const [selectedNames, setSelectedNames] = useState(() => new Map(selected.map((item) => [item.id, item])))
  const [searching, setSearching] = useState(false)
  const search = (page = 1, term = query) => {
    if (disabled || searching) return
    router.get(url.split('?')[0], { ...mcpListParams(filters, listPage), organization_query: term, organization_page: page }, {
      only: ['organization_options'], preserveState: true, preserveScroll: true, replace: true,
      onStart: () => setSearching(true), onFinish: () => setSearching(false),
    })
  }
  const toggle = (item: OrganizationOption) => {
    setSelectedNames((previous) => new Map(previous).set(item.id, item))
    onChange({ organization_ids: values.organization_ids.includes(item.id)
      ? values.organization_ids.filter((id) => id !== item.id) : [...values.organization_ids, item.id] })
  }
  return <fieldset className="space-y-3">
    <legend className="text-sm font-medium">{copy.organizations}</legend>
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={values.all_organizations} onChange={(event) => onChange({ all_organizations: event.target.checked })} />
      {copy.all_organizations}
    </label>
    <p className="text-xs text-muted-foreground">{values.all_organizations ? copy.future_organizations : copy.organizations_hint}</p>
    {!values.all_organizations && <>
      <div className="flex gap-2">
        <Input aria-label={copy.search_organizations} placeholder={copy.search_organizations} value={query} disabled={disabled}
          onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); search() } }} />
        <Button type="button" variant="outline" disabled={disabled || searching} onClick={() => search()}>{copy.search}</Button>
      </div>
      <div className="grid max-h-56 gap-2 overflow-y-auto rounded-md border border-border p-3 sm:grid-cols-2" aria-busy={searching}>
        {options.items.map((item) => <label key={item.id} className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={values.organization_ids.includes(item.id)} onChange={() => toggle(item)} />
          <span>{item.name}<span className="ml-1 text-xs text-muted-foreground">{item.slug}</span></span>
        </label>)}
        {!options.items.length && <p className="text-sm text-muted-foreground">{copy.no_organizations}</p>}
      </div>
      {options.total_pages > 1 && <div className="flex items-center justify-between gap-2 text-xs">
        <span>{copy.page} {options.page} {copy.of} {options.total_pages}</span>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" disabled={disabled || searching || options.page <= 1} onClick={() => search(options.page - 1, options.query)}>{copy.previous}</Button>
          <Button type="button" size="sm" variant="outline" disabled={disabled || searching || options.page >= options.total_pages} onClick={() => search(options.page + 1, options.query)}>{copy.next}</Button>
        </div>
      </div>}
      <div aria-live="polite" className="text-xs text-muted-foreground">
        <p>{copy.selected_organizations}: {values.organization_ids.length}</p>
        {!!values.organization_ids.length && <div className="mt-2 flex flex-wrap gap-2">{values.organization_ids.map((id) => {
          const item = selectedNames.get(id) || options.items.find((candidate) => candidate.id === id)
          return <button key={id} type="button" className="rounded border border-border px-2 py-1 hover:bg-accent"
            aria-label={`${copy.remove_organization}: ${item?.name || id}`} onClick={() => onChange({ organization_ids: values.organization_ids.filter((candidate) => candidate !== id) })}>
            {item?.name || id} ×
          </button>
        })}</div>}
      </div>
    </>}
  </fieldset>
}
